import { Button, Grid, Stack, Typography } from '@mui/material'
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Dropzone, { type DropzoneRef } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import ItemList from '../../Common/ItemList/ItemList'
import Image from './Image'
import ImageCardSkeleton from './ImageCardSkeleton'
import SearchBar from '../../Common/ItemList/SearchBar'
import AccountHeader from '../AccountHeader'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import { useSearchParams } from 'react-router'
import { type UserImageStore } from '../../UserData/_stores/UserImageStore'
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded'
import ImageListActions from './ImageListActions'
import ImageUploadQueue from './ImageUploadQueue'
import { ClearImageSelectedImage, SetImageSelectedImages } from '../../UIData/_actions/Account/AccountStoreActions'
import useShiftRangeSelection from '../../Common/_hooks/useShiftRangeSelection'
import useImageUploadQueue from './useImageUploadQueue'
import { ACCEPTED_IMAGE_UPLOAD_TYPES, getImageUploadUrl, MAX_IMAGE_UPLOAD_FILE_SIZE } from './imageUploadConfig'
import AccountFilterDrawer from '../Filters/AccountFilterDrawer'
import AccountFilterSection from '../Filters/AccountFilterSection'
import AccountLimitFilter from '../Filters/AccountLimitFilter'
import AccountSortFilter, { type AccountSortOption } from '../Filters/AccountSortFilter'
import { type ProductSortType } from '../../UserData/_stores/UserPrefsStore'
import { SaveUserPrefs } from '../../UserData/_actions/UserDataActions'
import AccountNoResults from '../AccountNoResults'

const SEARCH_FIELDS = ['search', 'sort']
const SORT_OPTIONS: ProductSortType[] = ['n-to-o', 'o-to-n', 'a-to-z', 'z-to-a']

export default function ImageList() {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')
  const [searchParams] = useSearchParams()

  const dropzoneRef = useRef<DropzoneRef>(null)

  const loading = useAppSelector(state => state.get('UIData').get('loaders').get('images'))
  const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))
  const userSort = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsSort'))
  const userLang = useAppSelector(state => state.get('userData').get('prefs').get('lang'))
  const imageSelectionClearCounter = useAppSelector(state => state.get('UIData').get('account').get('imageSelectionClearCounter'))

  const sortParam = searchParams.get('sort')
  const sort = sortParam
    ? sortParam.split(',').filter((value): value is ProductSortType => SORT_OPTIONS.includes(value as ProductSortType))
    : []
  const [search, setSearch] = useState({
    search: searchParams.get('search') ? searchParams.get('search')?.split(',') ?? [] : [],
    sort: sort.length > 0 ? sort : [userSort],
  })
  const defaultSearch = useMemo(() => ({
    search: [],
    sort: [userSort],
  }), [userSort])
  const [limit, setLimit] = useState(userLimit)
  const listId = useMemo(() => 'account-images', [])
  const selectionFilterKey = search.search.join(',')
  const searchText = useMemo(() => search.search.join(','), [search.search])
  const uploadUrl = useMemo(() => getImageUploadUrl(userLang ?? 'en_CA'), [userLang])
  const visibleImageItemIds = useAppSelector(state =>
    state.get('userData')
      .get('images')
      .get('itemLists')
      .get(listId)
      ?.get('items'))
  const visibleImageIds = useMemo(() => (
    visibleImageItemIds
      ?.valueSeq()
      .map(id => Number(id))
      .filter((id): id is number => Number.isFinite(id))
      .toArray() ?? []
  ), [visibleImageItemIds])
  const { getSelectionIds, resetSelectionRange } = useShiftRangeSelection(visibleImageIds)
  const imageUploadQueue = useImageUploadQueue({
    listId,
    uploadUrl,
  })

  const openDropzone = useCallback(() => {
    if (!dropzoneRef.current) return
    dropzoneRef.current.open()
  }, [])
  const headerActions = useMemo(() => (
    <Button
      variant="contained"
      color="accent"
      onClick={openDropzone}
      startIcon={<CloudUploadIcon />}
    >{t('Upload')}</Button>
  ), [openDropzone, t])

  const loadingGrid = useMemo(() => {
    const listSkeletons = []

    for (let i = 0; i < limit; i++) {
      listSkeletons.push(<ImageCardSkeleton key={i} />)
    }

    return listSkeletons
  }, [limit])

  useEffect(() => {
    resetSelectionRange()
    dispatch(ClearImageSelectedImage())
  }, [dispatch, resetSelectionRange, selectionFilterKey])

  useEffect(() => {
    resetSelectionRange()
  }, [imageSelectionClearCounter, resetSelectionRange])

  useEffect(() => {
    return () => {
      dispatch(ClearImageSelectedImage())
    }
  }, [dispatch])

  const onSelectImage = useCallback((id: number, shiftKey = false) => {
    dispatch(SetImageSelectedImages(getSelectionIds(id, shiftKey), true))
  }, [dispatch, getSelectionIds])

  const onChangeSearchText = useCallback((value: string) => {
    setSearch(search => ({
      ...search,
      search: value === '' ? [] : value.split(','),
    }))
  }, [])

  const onChangeSort = useCallback((nextSort: ProductSortType) => {
    setSearch(value => ({
      ...value,
      sort: [nextSort],
    }))

    if (nextSort === userSort) {
      return
    }

    const fd = new FormData()
    fd.append('accountProductsSort', nextSort)
    dispatch(SaveUserPrefs(fd, true))
  }, [dispatch, userSort])

  useEffect(() => {
    if (sortParam !== null) {
      return
    }

    setSearch(value => {
      if (value.sort[0] === userSort) {
        return value
      }

      return {
        ...value,
        sort: [userSort],
      }
    })
  }, [sortParam, userSort])

  const selectedSort = search.sort[0] ?? userSort
  const sortOptions: AccountSortOption<ProductSortType>[] = [
    { value: 'n-to-o', label: t('Newest first') },
    { value: 'o-to-n', label: t('Oldest first') },
    { value: 'a-to-z', label: t('A to Z') },
    { value: 'z-to-a', label: t('Z to A') },
  ]

  const onUnselectImage = useCallback((id: number, shiftKey = false) => {
    dispatch(SetImageSelectedImages(getSelectionIds(id, shiftKey), false))
  }, [dispatch, getSelectionIds])

  const renderItem = useCallback((item: UserImageStore) => {
    const imageId = Number(item.get('id'))

    return <Image
      item={item}
      highlighted={imageUploadQueue.recentUploadedImageIds.has(imageId)}
      onSelectImage={onSelectImage}
      onUnselectImage={onUnselectImage}
    />
  }, [imageUploadQueue.recentUploadedImageIds, onSelectImage, onUnselectImage])

  const renderList = useCallback((items: JSX.Element | null, links: JSX.Element | null, loading: boolean) => {
    return <>
      <Grid container spacing={{xs: 1, sm: 2}}>
        {loading
          ? loadingGrid
          : (items ? items : <Grid item xs={12}>
            <AccountNoResults />
          </Grid>)
        }
      </Grid>

      {links}
    </>
  }, [loadingGrid, t])

  return <>
    <AccountHeader
      title={t('Image Library')}
      actions={headerActions}
    />
    <div className={'images-container' + (loading ? ' images-container--loading' : '')}>
      <Dropzone
        ref={dropzoneRef}
        noClick
        noKeyboard
        multiple
        accept={ACCEPTED_IMAGE_UPLOAD_TYPES}
        maxSize={MAX_IMAGE_UPLOAD_FILE_SIZE}
        onDropAccepted={imageUploadQueue.onUploadAccepted}
        onDropRejected={imageUploadQueue.onUploadRejected}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div {...getRootProps({ style: { position: 'relative' } })}>
            <input {...getInputProps({ disabled: false })} />

            <SearchBar
              placeholder={t('Search your images...')}
              value={searchText}
              onChange={onChangeSearchText}
            >
              <AccountFilterDrawer>
                <AccountFilterSection title={t('Display')}>
                  <AccountSortFilter
                    label={t('Sort by')}
                    value={selectedSort}
                    options={sortOptions}
                    onChange={onChangeSort}
                  />
                  <AccountLimitFilter
                    label={t('Results per page')}
                    value={limit}
                    onChange={setLimit}
                  />
                </AccountFilterSection>
              </AccountFilterDrawer>
            </SearchBar>
            <ImageUploadQueue
              uploads={imageUploadQueue.uploads}
              onCancel={imageUploadQueue.onCancelUpload}
              onRetry={imageUploadQueue.onRetryUpload}
              onRemove={imageUploadQueue.onRemoveUpload}
              onClearCompleted={imageUploadQueue.onClearCompletedUploads}
              onClearFailed={imageUploadQueue.onClearFailedUploads}
            />
            <ImageListActions
              listId={listId}
              search={searchText}
            />
            <ItemList
              listId={listId}
              dataType={'images'}
              item={renderItem}
              searchQuery={search}
              defaultSearchQuery={defaultSearch}
              searchFields={SEARCH_FIELDS}
              limit={limit}
              render={renderList}
            />
            {isDragActive ? <Stack spacing={1} sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Typography variant="h2" component="h3">{t('Drop your files here')}</Typography>
              <SaveAltRoundedIcon
                color="secondary"
                sx={{
                  fontSize: '4rem',
                }}
              />
            </Stack> : null}
          </div>
        )}
      </Dropzone>
    </div>
  </>
}
